/** @jsx isolateComponent */
import { ServiceIconShell } from '../ServiceIconShell';
import { isolateComponent } from '@zapier/style-encapsulation';
import { Img } from '../../display/Img';

import { ServiceIconSize, ServiceIconConfig } from './constants';

type ServiceIconProps = {
  /**
   * Inform screen-readers that they should ignore the image. Image will still be visually displayed.
   *
   * @default false
   */
  ariaHidden?: boolean;
  /**
   * The name of the service whose icon is being displayed (used as alt text for the icon), e.g., "Google Sheets".
   */
  serviceName: string;
  /**
   * A URL for the service icon image to be rendered. If this is an Imgix URL, a large-format image can be used and the image will automatically be resized.
   */
  src: string;
  /**
   * Sets the width and height of the icon (since it is a square), in pixels.
   * The list of possible sizes is finite to enforce consistency across our UI.
   */
  size: ServiceIconSize;
  /**
   * Applies a shadow to the ServiceIcon. It is recommended to use `hasShadow`
   * at sizes 80px and larger.
   */
  hasShadow?: boolean;
  /**
   * Controls the presence/absence of the border. It is recommended that a borderless ServiceIcon be contained in a larger, bordered container.
   */
  hasBorder?: boolean;
  /**
   * Applies a custom alt attribute to the underlying Img component.
   */
  alt?: string;
  /**
   * Changes the background color to white (#fff)
   */
  hasWhiteBackground?: boolean;
};

const getPropsWithDefaults = (props: ServiceIconProps) => ({
  ariaHidden: false,
  hasBorder: true,
  ...props,
});

/**
 * Renders an icon representing a service (aka app, aka integration).
 */
export const ServiceIcon = (props: ServiceIconProps) => {
  const {
    ariaHidden,
    serviceName,
    hasBorder,
    hasShadow,
    size,
    src,
    alt,
    hasWhiteBackground,
  } = getPropsWithDefaults(props);
  const { iconSize } = ServiceIconConfig[size];
  return (
    <ServiceIconShell
      size={size}
      hasBorder={hasBorder}
      hasShadow={hasShadow}
      hasWhiteBackground={hasWhiteBackground}
    >
      <Img
        alt={alt || `${serviceName} logo`}
        ariaHidden={ariaHidden}
        borderRadius={0}
        height={iconSize}
        objectFit="contain"
        src={src}
        width={iconSize}
      />
    </ServiceIconShell>
  );
};
